import { blockElementsSet, floatingElementsSet } from './flatChildrens'

/** DOM subtree root that supports `querySelector` / `querySelectorAll`. */
type QuerySelectorRoot = Document | Element | DocumentFragment

/**
 * Position object stored in an element's `responsiveStyles.large.position`
 * for floating (draggable) elements. Coordinates are interpreted relative to
 * the element's nearest positioned ancestor — which, after re-parenting, is
 * the target block's `<section>` wrapper (via its `transform: translateZ(0)`).
 */
export interface FloatingElementPosition {
  top: number
  left: number
  bottom: number
  right: number
  preferBottom: false
  preferRight: false
}

/** Minimal shape accepted by the helpers — both IElement and plain objects match. */
interface ElementLike {
  id?: string
  type?: string
  responsiveStyles?: { large?: { dimensions?: { width?: number; height?: number } } }
}

/**
 * Finds the smallest block element (Text / Image / Video / Table / ProductList)
 * whose bounding box contains the given viewport point and returns its element id.
 *
 * Used when a floating element (Signature / TextField / DateField / Initials /
 * Checkbox / SaveCardDetails) is dropped from the "Add element" tray onto the
 * page. If the drop lands over a block, the new element should become a child
 * of that block — mirroring the behaviour that already happens when an existing
 * floating element is dragged onto a block via the moveable wrapper.
 *
 * Important: rows and columns are *not* candidates. Only leaf block elements
 * are wrapped in `<section class="boundBox" data-type="...">` by
 * `ElementDropContainer`; rows/columns render as plain `<div>`s, so the
 * selector below cannot match them. As a second safeguard we also filter by
 * `blockElementsSet`, which excludes ROW/COLUMN/SIGNATURE/PAGE_SECTION etc.
 *
 * Always pass `root` when you know which region received the drop (the
 * `.page-section` element for header/footer, the `.page-content` element for
 * main content). Without it, a drop in the header can match a block in the
 * content area (and vice versa) if coordinates happen to overlap — e.g. when
 * two pages are visible side-by-side, or when a content block has a rect
 * that happens to overlap the header's viewport region because of scrolling,
 * transforms, or stale measurements.
 *
 * @param clientX            Viewport X coordinate (e.g. `event.clientX`).
 * @param clientY            Viewport Y coordinate (e.g. `event.clientY`).
 * @param options.root       DOM subtree to search within. Defaults to `document`.
 * @param options.excludeId  Element id to skip — typically the just-added element
 *                           so we don't reparent it under itself.
 * @returns The id of the innermost matching block, or `null` if the drop point
 *          isn't over any eligible block within `root`.
 */
export function findBlockIdAtPoint(
  clientX: number,
  clientY: number,
  options: { root?: QuerySelectorRoot | null; excludeId?: string } = {}
): string | null {
  const { root, excludeId } = options
  const searchRoot: QuerySelectorRoot = root ?? document
  const blocks = searchRoot.querySelectorAll('section.boundBox[data-id][data-type]')

  let candidateId: string | null = null
  let smallestArea = Infinity

  blocks.forEach(block => {
    const id = block.getAttribute('data-id')
    if (!id || id === excludeId) return

    const type = block.getAttribute('data-type')
    if (!type || !blockElementsSet.has(type as never)) return

    const rect = (block as HTMLElement).getBoundingClientRect()
    if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) return

    const area = rect.width * rect.height
    // Prefer the innermost (smallest) block in case of nesting.
    if (area < smallestArea) {
      smallestArea = area
      candidateId = id
    }
  })

  return candidateId
}

/**
 * Given a block's id and a viewport point, computes the position object that
 * would place the element at that point *relative to the block's `<section>`*.
 *
 * After `moveElementById` reparents a floating element under a block, the
 * element's positioning context changes from the page/section to the block's
 * section (because that `<section>` has `transform: translateZ(0)`). The
 * existing `position` was computed relative to the old ancestor, so it must be
 * recomputed — otherwise the stored top/left values place the element far from
 * where the user actually dropped it, until the user drags it and the moveable
 * wrapper recalculates via `getNearestSnap`.
 *
 * @returns The position object to write into the element's `responsiveStyles`,
 *          or `null` if the block's DOM node can't be found under `root`.
 */
export function computeBlockRelativePosition(
  blockId: string,
  clientX: number,
  clientY: number,
  element?: ElementLike | null,
  root?: QuerySelectorRoot | null
): FloatingElementPosition | null {
  const searchRoot: QuerySelectorRoot = root ?? document
  const blockEl = searchRoot.querySelector(`section.boundBox[data-id="${CSS.escape(blockId)}"]`) as HTMLElement | null
  if (!blockEl) return null

  const rect = blockEl.getBoundingClientRect()
  const dims = element?.responsiveStyles?.large?.dimensions
  const width = typeof dims?.width === 'number' ? dims.width : 0
  const height = typeof dims?.height === 'number' ? dims.height : 0

  const top = clientY - rect.top
  const left = clientX - rect.left

  return {
    top,
    left,
    bottom: Math.max(0, rect.height - top - height),
    right: Math.max(0, rect.width - left - width),
    preferBottom: false,
    preferRight: false,
  }
}

/**
 * High-level helper used by "add element" drop handlers.
 *
 * If `element` is a floating/fillable element and the drop point lands over an
 * eligible block within `root`, returns `{ blockId, position }` — where
 * `position` is already block-relative and ready to write into
 * `responsiveStyles.large.position`. Otherwise returns `null`.
 *
 * Callers still invoke their own store actions for reparenting and style
 * updates (those signatures differ between main-content and section callers),
 * but the DOM/geometry work is centralised here.
 */
export function resolveDropTargetBlock(
  clientX: number,
  clientY: number,
  element?: ElementLike | null,
  root?: QuerySelectorRoot | null
): { blockId: string; position: FloatingElementPosition } | null {
  if (!element?.id || !element.type || !floatingElementsSet.has(element.type as never)) return null

  const blockId = findBlockIdAtPoint(clientX, clientY, { root, excludeId: element.id })
  if (!blockId) return null

  const position = computeBlockRelativePosition(blockId, clientX, clientY, element, root)
  if (!position) return null

  return { blockId, position }
}
